import React from 'react'
import { IImageAsset } from '../../modules/images/types'
import {
  AnswerID,
  IQuestionModelState,
  IQuestionSchema,
  IResponseValidation,
  IQuestionViewProps,
  QuestionModelTypes,
  QuestionType
} from '../types'
import { IVideoInternalAsset } from '../../modules/videos/types'

export const EMPTY_SUBMIT_RESPONSE: EcommerceSubmitResponse = []
export type EcommerceStoreType =
  | 'amazonw'
  | 'walmartw'
  | 'targetw'
  | 'ecommercew'
  | 'nikew'
  | 'costcow'
  | 'chewyw'
  | 'krogerw'
  | 'samsclubw'
  | 'tescow'
export enum ASSETS_TYPES {
  MODEL = 'model',
  VIDEO = 'video',
  IMAGE = 'image'
}
export type EcommerceBannerPlacement = 'banner' | 'featured' | 'product_page' | 'footer'
export type EcommerceModeType = 'shopping' | 'find' | 'focus'
export type EcommerceStockType = 'full' | 'typical' | 'low' | 'no'
export type EcommerceNikeItemSize = 'clothing' | 'footwear'
export type EcommerceImageType = 'additional' | 'main' | 'single'
export type EcommerceStoreViews = Record<EcommerceStoreType, IStoreView>
export interface IEcommerceProductGallery extends IImageAsset {
  product: {
    height: number
    width: number
    image_type: EcommerceImageType
    default?: boolean
  }
}

export interface IFileAsset {
  file_url: string
  default: boolean
  file_name?: string
}

export interface IEcommerceAnswerSchema {
  description: string | null
  feature_bullets: string | null
  discounted_price: number
  per_count_price?: string
  available: boolean
  na?: boolean
  id: AnswerID
  image: IImageAsset
  view3d?: IFileAsset
  main_image_height: number
  main_image_offset_y: number
  price: number
  text: string
  width: number
  units?: number
  bought?: string
  reviews?: number
  rating?: number
  best_seller?: string
  sub_title?: string
  green_label?: string
  eco_label?: string
  red_label?: string
  subscribe_percent?: number
  subscribe_coupon_percent?: number
  subscribe_repeat_percent?: number
  prime?: number
  amazon_choice?: boolean
  ingredient_statement?: string
  allergens_description?: string
  nutrition_information?: { name: string; value: string; hero?: boolean }[]
  sponsored?: boolean
  overal_pick?: boolean
  featured?: boolean
  banner?: boolean
  right_column?: boolean
  footer?: boolean
  focused?: boolean
  index?: number
  gallery?: IEcommerceProductGallery[]
  item_size?: EcommerceNikeItemSize
  not_apply_filter?: boolean
  parent_product?: string
  market_place_id?: string
  children?: IEcommerceAnswerSchema[] // served nested child SKUs; flattened at mount (utils/familyTree.ts)
  hidden?: boolean // researcher-preview show/hide marks (utils/familyTree.ts markHiddenProducts)
  fakeHidden?: boolean
  rawText?: string // text/image before preview substitution (Component.tsx)
  rawImage?: IImageAsset
  // legacy single-axis scalar fields — kept for old surveys not yet migrated
  dimension_name?: string
  dimension_value?: string
  // new multi-axis array (EC product family pilot, AYTM-12710)
  dimensions?: DimensionEntry[]
}

export type DimensionEntry = { name: string; value: string; valueOrder?: string[] }
export interface IEcommerceQuestionSchema extends IQuestionSchema {
  type: QuestionType.SHELF_TEST
  answers: IEcommerceAnswerSchema[]
  max_cart_size: number
  max_money_spent: number
  max_qty_per_product: number
  min_qty: number
  na?: true
  price_mask: string
  shelf_width: number[]
  shelves_amount: number
  shopping_cart?: true
  show_name?: true
  search_condition?: string
  instruction?: string
  hide_price?: true
  stock_type: EcommerceStockType
  mode_type: EcommerceModeType
  products_per_row?: number
  products_per_row_mobile?: number
  store: EcommerceStoreType
  grid_variants?: boolean
  name?: string
  ad_company_description?: string
  ad_company_title?: string
  ad_image1?: IImageAsset
  ad_image2?: IImageAsset
}

export interface IEcommerceAnswerResponse {
  id: AnswerID
  v: number
  subscribe?: number
  price?: number
  selected?: boolean
}

export type EcommerceResponse = {
  answers: IEcommerceAnswerResponse[]
  clicked?: boolean
  selected?: AnswerID
}

export type EcommerceSubmitResponse = {
  id: AnswerID
  v: number
  subscribe?: number
}[]

export interface IEcommerceVideoAsset {
  image: {
    origginal_path?: string
    original_url?: string
  }
  product: {
    default: boolean
  }
  trimmed_path: string
  video: IVideoInternalAsset
}

export interface IEcommerceResponseValidation extends IResponseValidation {
  //
}

export interface EcommerceQuestionModelTypes extends QuestionModelTypes {
  schema: IEcommerceQuestionSchema
  response: EcommerceResponse
  responseValidation: IEcommerceResponseValidation
  submitResponse: EcommerceSubmitResponse
}

export interface IEcommerceQuestionModelState extends IQuestionModelState<EcommerceQuestionModelTypes> {}

export interface IEcommerceQuestionViewProps extends IQuestionViewProps<EcommerceQuestionModelTypes> {
  parentRef: React.RefObject<HTMLDivElement>
  checkout?: () => void
  setClosed?: () => void
}
export interface EcommerceCartProps {
  response: EcommerceResponse
  schema: IEcommerceQuestionSchema
  showCart: () => void
  onUpdateResponse?: (response: EcommerceResponse) => void
  cartShown?: boolean
  totals?: number[]
  closeCart?: () => void
  checkout?: () => void
  scrollTop?: () => void
}

export interface IItemPreviewProps {
  schema: IEcommerceAnswerSchema
  available?: boolean
  questionSchema: IEcommerceQuestionSchema
  onAddToCart?: (qty?: number) => void
  onItemSelect: () => void
  width?: number
  findability?: boolean
  response?: ArrayElementType<EcommerceResponse['answers']>
  index?: number
  className?: string
  onProductAction?: (qnt: -1 | 1, index: string, subscribe?: number) => void
  onSpin?: (event: React.KeyboardEvent, index: string, subscribe?: number) => void
  saveScrollPoint?: (event: React.MouseEvent) => void
  disabled?: boolean
  vertical?: boolean
  bannerPlacement?: EcommerceBannerPlacement
  bannerSource?: string
  'data-testid'?: string
}

export interface IItemProps {
  schema: IEcommerceAnswerSchema
  questionSchema: IEcommerceQuestionSchema
  findability?: boolean
  qty?: number
  totals?: number[]
  response: EcommerceResponse
  onAddToCart: (qty?: number, subscribe?: number) => void
  onBuyNow?: (qty: number) => void
  onClose: () => void
  scrollTop?: () => void
  onItemSelect?: (index: number) => void
  rightColumnProductIndex?: number
  onBannerClick?: () => void
  onProductAction?: (qnt: -1 | 1, index: string, subscribe?: number) => void
  onSpin?: (event: React.KeyboardEvent, index: string, subscribe?: number) => void
  saveScrollPoint?: (event: React.MouseEvent) => void
}

export type assetsCollectionElement = IImageAsset | IFileAsset | IEcommerceVideoAsset | IEcommerceProductGallery

export interface IDeliveryDate {
  phrase: string
  date: string
  originalDate: Date
}

export interface IStoreView {
  View: React.ComponentType<IEcommerceQuestionViewProps>
  name: string
}

// status - remove
// locked_config - skipped
// collapsed_config - skipped
// answers.type - remove
// answers.fixedPosition - remove
